import type React from "react";
import { useState, useEffect } from "react";
import type {
  Tenant,
  CustomDomain,
  TenantBilling,
  AuditLogEntry,
  CloudflareEnvironment,
  TenantStatus,
  PlatformAdmin,
} from "./types/index.js";
import { Header } from "./components/Header.js";
import { HardGateBanner } from "./components/HardGateBanner.js";
import { TenantCatalogTable } from "./components/TenantCatalogTable.js";
import { CustomDomainManager } from "./components/CustomDomainManager.js";
import { BillingTierCard } from "./components/BillingTierCard.js";
import { AuditLogFeed } from "./components/AuditLogFeed.js";

/**
 * Resolves Cloudflare Access identity and assertion headers when available.
 */
export function getCloudflareAccessHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  if (typeof window === "undefined") return headers;

  const getCookie = (name: string): string | null => {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  };

  const jwt =
    getCookie("CF_Authorization") ||
    localStorage.getItem("CF_Authorization") ||
    localStorage.getItem("CF-Access-Jwt-Assertion") ||
    localStorage.getItem("cf-access-jwt") ||
    localStorage.getItem("cf_access_token") ||
    sessionStorage.getItem("CF_Authorization");

  if (jwt) {
    headers["Cf-Access-Jwt-Assertion"] = jwt;
    headers["Authorization"] = `Bearer ${jwt}`;
  }

  const clientId =
    localStorage.getItem("CF_ACCESS_CLIENT_ID") ||
    localStorage.getItem("cf-access-client-id");
  const clientSecret =
    localStorage.getItem("CF_ACCESS_CLIENT_SECRET") ||
    localStorage.getItem("cf-access-client-secret");

  if (clientId && clientSecret) {
    headers["CF-Access-Client-Id"] = clientId;
    headers["CF-Access-Client-Secret"] = clientSecret;
  }

  const userEmail =
    localStorage.getItem("CF_ACCESS_USER_EMAIL") ||
    localStorage.getItem("cf-access-user-email");
  if (userEmail) {
    headers["Cf-Access-Authenticated-User-Email"] = userEmail;
  }

  return headers;
}

/**
 * Fetch wrapper that automatically attaches Cloudflare Access identity / token headers.
 */
export async function fetchWithAccess(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const accessHeaders = getCloudflareAccessHeaders();
  const headers = new Headers(init?.headers);

  for (const [key, value] of Object.entries(accessHeaders)) {
    if (!headers.has(key)) {
      headers.set(key, value);
    }
  }

  return fetch(input, {
    ...init,
    headers,
  });
}

const PROD_ENV: CloudflareEnvironment = {
  name: "Production",
  accountId: "be728a3f7c20dd3d0314375fac1fee64",
  zone: "downline.app",
  apiOrigin: "https://downline-instance-production.prod-downline.workers.dev",
  d1Database: "downline-instance-catalog-production",
  secretsStore: "downline-instance-production",
  hardGateEnforced: true,
};

const STAGING_ENV: CloudflareEnvironment = {
  name: "Staging",
  accountId: "944fa03010ed8ba5a83055ad6b7f1fcb",
  zone: "staging.downline.app",
  apiOrigin: "https://downline-instance-staging.barrconsultants-downline.workers.dev",
  d1Database: "downline-instance-catalog-staging",
  secretsStore: "downline-instance-staging",
  hardGateEnforced: true,
};

export function getApiBase(env: CloudflareEnvironment): string {
  if (typeof window !== "undefined") {
    const custom = localStorage.getItem(`API_BASE_${env.name.toUpperCase()}`);
    if (custom) return custom.replace(/\/+$/, "");
    if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
      return "";
    }
  }
  return env.apiOrigin ?? `https://${env.zone}`;
}

const INITIAL_BILLING: TenantBilling[] = [
  {
    tenantId: "00000000-0000-0000-0000-00000000f0d1",
    stripeCustomerId: "cus_hom_founders_test",
    planTier: "FOUNDERS",
    billingStatus: "active",
    stripeSubscriptionId: "sub_hom_inaugural_2026",
    currentPeriodEnd: "2027-10-01T00:00:00Z",
  },
  {
    tenantId: "00000000-0000-4000-8000-000000000001",
    stripeCustomerId: "cus_acme_starter_test",
    planTier: "STARTER",
    billingStatus: "active",
    stripeSubscriptionId: "sub_acme_starter_monthly",
    currentPeriodEnd: "2026-11-01T00:00:00Z",
  },
];

export const App: React.FC = () => {
  const [currentEnv, setCurrentEnv] = useState<CloudflareEnvironment>(PROD_ENV);
  const [activeTab, setActiveTab] = useState<"tenants" | "domains" | "billing" | "audit">("tenants");
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [domains, setDomains] = useState<CustomDomain[]>([]);
  const [billingList] = useState<TenantBilling[]>(INITIAL_BILLING);
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const handleSwitchEnv = (env: "Production" | "Staging") => {
    setCurrentEnv(env === "Production" ? PROD_ENV : STAGING_ENV);
  };

  const syncRemoteCatalog = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const apiBase = getApiBase(currentEnv);
      const [tenantsRes, auditRes, domainsRes] = await Promise.all([
        fetchWithAccess(`${apiBase}/v1/admin/tenants`),
        fetchWithAccess(`${apiBase}/v1/admin/audit`),
        fetchWithAccess(`${apiBase}/v1/admin/domains`),
      ]);

      const errMessages: string[] = [];

      if (tenantsRes.ok) {
        const data = (await tenantsRes.json()) as { tenants?: any[] };
        const loaded: Tenant[] = (data?.tenants || []).map((t: any) => ({
          id: t.id,
          slug: t.slug,
          status: t.status,
          neonSecretRef: t.neonSecretRef ?? t.neon_secret_ref ?? "",
          schemaVersion: t.schemaVersion ?? t.schema_version ?? "0",
          createdAt: t.createdAt ?? t.created_at ?? "",
        }));
        setTenants(loaded);
      } else {
        const errJson = await tenantsRes.json().catch(() => ({}));
        errMessages.push(`Tenants (${tenantsRes.status}): ${(errJson as any)?.error?.message || tenantsRes.statusText || "request failed"}`);
      }

      if (auditRes.ok) {
        const data = (await auditRes.json()) as { logs?: any[]; auditLogs?: any[] };
        const list = data?.logs || data?.auditLogs || [];
        const loaded: AuditLogEntry[] = list.map((l: any) => ({
          id: l.id,
          actorId: l.actorId ?? l.actor_id,
          action: l.action,
          tenantId: l.tenantId ?? l.tenant_id,
          detailJson: typeof l.detailJson === "string" ? l.detailJson : (l.detail_json ? (typeof l.detail_json === "string" ? l.detail_json : JSON.stringify(l.detail_json)) : undefined),
          createdAt: l.createdAt ?? l.created_at ?? "",
        }));
        setAuditLogs(loaded);
      } else {
        const errJson = await auditRes.json().catch(() => ({}));
        errMessages.push(`Audit Logs (${auditRes.status}): ${(errJson as any)?.error?.message || auditRes.statusText || "request failed"}`);
      }

      if (domainsRes.ok) {
        const data = (await domainsRes.json()) as { domains?: any[] };
        const loaded: CustomDomain[] = (data?.domains || []).map((d: any) => ({
          id: d.id,
          tenantId: d.tenantId ?? d.tenant_id,
          hostname: d.hostname,
          status: d.status,
          verificationTxtName: d.verificationTxtName ?? d.verification_txt_name,
          verificationTxtValue: d.verificationTxtValue ?? d.verification_txt_value,
          sslStatus: d.sslStatus ?? d.ssl_status,
          createdAt: d.createdAt ?? d.created_at ?? "",
        }));
        setDomains(loaded);
      } else {
        const errJson = await domainsRes.json().catch(() => ({}));
        errMessages.push(`Custom Domains (${domainsRes.status}): ${(errJson as any)?.error?.message || domainsRes.statusText || "request failed"}`);
      }

      if (errMessages.length > 0) {
        setError(errMessages.join(" | "));
      }
    } catch (err) {
      setError((err as Error).message || "Failed to connect to control plane API");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void syncRemoteCatalog();
  }, [currentEnv]);

  const handleToggleTenantStatus = async (tenantId: string, currentStatus: TenantStatus) => {
    const nextStatus: TenantStatus = currentStatus === "active" ? "suspended" : "active";
    const apiBase = getApiBase(currentEnv);
    try {
      const res = await fetchWithAccess(`${apiBase}/v1/admin/lifecycle`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenantId,
          status: nextStatus,
          actorId: "usr_platform_admin_jacob",
        }),
      });

      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error((errJson as any)?.error?.message || `HTTP ${res.status}`);
      }

      setTenants((prev) =>
        prev.map((t) => (t.id === tenantId ? { ...t, status: nextStatus } : t)),
      );

      const newAudit: AuditLogEntry = {
        id: `aud-${Date.now()}`,
        actorId: "usr_platform_admin_jacob",
        action: nextStatus === "suspended" ? "TENANT_SUSPENDED" : "TENANT_RESUMED",
        tenantId,
        detailJson: JSON.stringify({ reason: "Administrative toggle via Platform Admin Console" }),
        createdAt: new Date().toISOString(),
      };
      setAuditLogs((prev) => [newAudit, ...prev]);
    } catch (err) {
      setError(`Failed to update tenant status: ${(err as Error).message}`);
    }
  };

  const handleToggleDomainStatus = async (domainId: string, currentStatus: string) => {
    const nextStatus = currentStatus === "active" ? "suspended" : "active";
    const apiBase = getApiBase(currentEnv);
    try {
      const res = await fetchWithAccess(`${apiBase}/v1/tenant/domains`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domainId, status: nextStatus }),
      });
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error((errJson as any)?.error?.message || `HTTP ${res.status}`);
      }
      setDomains((prev) =>
        prev.map((d) => (d.id === domainId ? { ...d, status: nextStatus } : d)),
      );
    } catch (err) {
      setError(`Failed to toggle domain status: ${(err as Error).message}`);
    }
  };

  const handleRecheckDomain = async (domainId: string) => {
    const apiBase = getApiBase(currentEnv);
    try {
      const res = await fetchWithAccess(`${apiBase}/v1/admin/domains?domain_id=${encodeURIComponent(domainId)}`);
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error((errJson as any)?.error?.message || `HTTP ${res.status}`);
      }
      setDomains((prev) =>
        prev.map((d) =>
          d.id === domainId ? { ...d, status: "active", sslStatus: "active" } : d,
        ),
      );

      const newAudit: AuditLogEntry = {
        id: `aud-${Date.now()}`,
        actorId: "usr_platform_admin_jacob",
        action: "DOMAIN_RECHECK_VERIFIED",
        detailJson: JSON.stringify({ domainId, result: "SSL Issued, DNS CNAME Active" }),
        createdAt: new Date().toISOString(),
      };
      setAuditLogs((prev) => [newAudit, ...prev]);
    } catch (err) {
      setError(`Failed to recheck domain: ${(err as Error).message}`);
    }
  };

  const handleOpenStripePortal = async (tenantId: string) => {
    const apiBase = getApiBase(currentEnv);
    const billing = billingList.find((b) => b.tenantId === tenantId);
    const fallbackUrl = billing
      ? `https://billing.stripe.com/p/session/test_${billing.stripeCustomerId}?return_url=${encodeURIComponent(`${apiBase}/admin`)}`
      : undefined;

    try {
      const res = await fetchWithAccess(`${apiBase}/v1/create-billing-portal-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenantId,
          returnUrl: `${apiBase}/admin`,
        }),
      });

      if (res.ok) {
        const data = (await res.json()) as { portalUrl?: string; url?: string; sessionUrl?: string };
        const targetUrl = data.portalUrl || data.url || data.sessionUrl;
        if (targetUrl) {
          window.open(targetUrl, "_blank");
          return;
        }
      }

      if (fallbackUrl) {
        window.open(fallbackUrl, "_blank");
      } else {
        const errJson = await res.json().catch(() => ({}));
        throw new Error((errJson as any)?.error?.message || `HTTP ${res.status} portal session creation failed`);
      }
    } catch (err) {
      if (fallbackUrl) {
        window.open(fallbackUrl, "_blank");
      } else {
        setError(`Failed to open billing portal: ${(err as Error).message}`);
      }
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#020617",
        color: "#e2e8f0",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      <HardGateBanner currentEnv={currentEnv} onSwitchEnv={handleSwitchEnv} />
      <Header activeTab={activeTab} onTabChange={setActiveTab} />

      <main style={{ maxWidth: "1280px", margin: "0 auto" }}>
        {error && (
          <div
            role="alert"
            style={{
              margin: "16px 24px",
              padding: "14px 18px",
              backgroundColor: "rgba(127, 29, 29, 0.4)",
              border: "1px solid #ef4444",
              borderRadius: "6px",
              color: "#fca5a5",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: "14px",
            }}
          >
            <span>
              <strong>Platform API Error:</strong> {error}
            </span>
            <button
              type="button"
              onClick={() => void syncRemoteCatalog()}
              style={{
                backgroundColor: "#7f1d1d",
                color: "#fee2e2",
                border: "1px solid #b91c1c",
                borderRadius: "4px",
                padding: "4px 10px",
                cursor: "pointer",
                fontSize: "12px",
              }}
            >
              Retry
            </button>
          </div>
        )}

        {isLoading ? (
          <div
            style={{
              padding: "60px 24px",
              textAlign: "center",
              color: "#94a3b8",
              fontSize: "14px",
            }}
          >
            Loading platform control plane data from {currentEnv.name} ({currentEnv.zone})...
          </div>
        ) : (
          <>
            {activeTab === "tenants" && (
              <TenantCatalogTable
                tenants={tenants}
                onSelectTenant={(t) => setActiveTab("billing")}
                onToggleStatus={handleToggleTenantStatus}
              />
            )}
            {activeTab === "domains" && (
              <CustomDomainManager
                domains={domains}
                onRecheckVerification={handleRecheckDomain}
                onToggleDomainStatus={handleToggleDomainStatus}
              />
            )}
            {activeTab === "billing" && (
              <BillingTierCard
                billingList={billingList}
                onOpenPortal={handleOpenStripePortal}
              />
            )}
            {activeTab === "audit" && <AuditLogFeed logs={auditLogs} />}
          </>
        )}
      </main>
    </div>
  );
};
